import {
  FormField,
  TextInput,
  DatePicker,
  Autocomplete,
} from '@dev-spendesk/grapes';
import { type FormikProps } from 'formik';
import { useMemo, useState } from 'react';

import { type BusinessRepresentative } from './BusinessRepresentative';
import { PhoneNumberInput } from '../../../../../common/components/PhoneNumberInput';
import { useTranslation } from '../../../../../common/hooks/useTranslation';
import { COUNTRIES } from '../../../../../config/country';

export function BusinessRepresentativeForm({
  form,
}: {
  form: FormikProps<BusinessRepresentative>;
}) {
  const { t } = useTranslation('global');

  // Map the country map into the autocomplete options with translated keys.
  const unfilteredCountryOptions = useMemo(() => {
    return Object.values(COUNTRIES).map((it) => ({
      key: it.alpha2,
      label: t(it.translationKey),
    }));
  }, []);

  const [countryOptions, setCountryOptions] = useState(
    unfilteredCountryOptions,
  );

  return (
    <form
      onSubmit={form.handleSubmit}
      className="mt-16 mr-16 ml-16 flex flex-col items-stretch gap-24"
    >
      <div className="flex gap-16">
        <FormField
          label={t('forms.firstName.label')}
          className="text-start"
          alertMessage={
            form.touched.firstName ? form.errors.firstName : undefined
          }
        >
          <TextInput
            name="firstName"
            min={1}
            onChange={form.handleChange}
            value={form.values.firstName}
            placeholder="First name"
            onBlur={form.handleBlur}
          />
        </FormField>

        <FormField
          label={t('forms.lastName.label')}
          className="text-start"
          alertMessage={
            form.touched.lastName ? form.errors.lastName : undefined
          }
        >
          <TextInput
            name="lastName"
            min={1}
            onChange={form.handleChange}
            value={form.values.lastName}
            placeholder="Last name"
            onBlur={form.handleBlur}
          />
        </FormField>
      </div>

      <FormField
        label={t('forms.addressFirstLine.label')}
        className="text-start"
        alertMessage={
          form.touched.address?.addressFirstLine
            ? form.errors.address?.addressFirstLine
            : undefined
        }
      >
        <TextInput
          textAlign="left"
          name="address.addressFirstLine"
          min={1}
          onChange={form.handleChange}
          value={form.values.address.addressFirstLine}
          placeholder="First line"
          onBlur={form.handleBlur}
        />
      </FormField>

      <div className="flex flex-row gap-16">
        <FormField
          className="flex-2 text-start"
          label={t('forms.city.label')}
          alertMessage={
            form.touched.address?.city ? form.errors.address?.city : undefined
          }
        >
          <TextInput
            name="address.city"
            min={1}
            onChange={form.handleChange}
            value={form.values.address.city}
            placeholder="City"
            onBlur={form.handleBlur}
          />
        </FormField>

        <FormField
          label={t('forms.postCode.label')}
          className="text-start"
          alertMessage={
            form.touched.address?.postCode
              ? form.errors.address?.postCode
              : undefined
          }
        >
          <TextInput
            name="address.postCode"
            min={1}
            onChange={form.handleChange}
            value={form.values.address.postCode}
            placeholder="Post code"
            onBlur={form.handleBlur}
          />
        </FormField>
      </div>

      <FormField
        className="flex-grow text-start"
        label={t('forms.country.label')}
        alertMessage={
          form.touched.address?.countryIso2Code
            ? form.errors.address?.countryIso2Code
            : undefined
        }
      >
        <Autocomplete
          fit="parent"
          name="address.countryIso2Code"
          options={countryOptions}
          onSelect={(option) => {
            if (option) {
              form.setFieldValue('address.countryIso2Code', option?.key);
            }
          }}
          onSearch={(search) => {
            if (!search) {
              setCountryOptions(unfilteredCountryOptions);
              return;
            }
            setCountryOptions(
              unfilteredCountryOptions.filter((option) =>
                option.label.toLowerCase().includes(search.toLowerCase()),
              ),
            );
          }}
          value={
            form.values.address.countryIso2Code
              ? {
                  key: form.values.address.countryIso2Code,
                  label:
                    t(
                      COUNTRIES[form.values.address.countryIso2Code]
                        ?.translationKey,
                    ) ?? '',
                }
              : undefined
          }
          placeholder={t('forms.country.placeholder')}
        />
      </FormField>

      <FormField
        label={t('forms.birthDate.label')}
        className="text-start"
        alertMessage={form.errors.dateOfBirth ? 'Required' : undefined}
      >
        <DatePicker
          value={form.values.dateOfBirth}
          onChange={(date) => {
            if (date) {
              form.setFieldValue('dateOfBirth', date);
            }
          }}
        />
      </FormField>

      <FormField
        label={t('forms.phoneNumber.label')}
        className="text-start"
        alertMessage={
          form.touched.phoneNumber ? form.errors.phoneNumber : undefined
        }
      >
        <PhoneNumberInput
          name="phoneNumber"
          fit="parent"
          initialCountry="GB"
          onChange={(phoneNumber) => {
            form.setFieldValue('phoneNumber', phoneNumber);
          }}
          value={form.values.phoneNumber}
        />
      </FormField>

      <FormField
        label={t('forms.email.label')}
        className="text-start"
        alertMessage={form.errors.email}
      >
        <TextInput
          name="email"
          min={1}
          onChange={form.handleChange}
          value={form.values.email}
          placeholder="Email"
        />
      </FormField>
    </form>
  );
}
